class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.defaultContent = this.querySelector('[data-search-default-content]');
    // The default content is fetched after load (see loadDefaultContent); the slot marks where it goes
    this.defaultSlot = this.querySelector('[data-search-default-slot]');
    this.isOpen = false;

    this.setupEventListeners();
    if (!this.defaultContent && this.defaultSlot) this.scheduleDefaultContent();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.defaultContent) this.bindDefaultContent();
  }

  bindDefaultContent() {
    this.defaultContent.addEventListener('mousedown', (event) => {
      // Prevent focus from leaving the search input on all interactions.
      // Without this, onFocusOut fires and hides the content before
      // click events can complete (especially in Safari where <a> tags
      // don't receive focus on click).
      event.preventDefault();
    });

    this.defaultContent.addEventListener('click', (event) => {
      const pill = event.target.closest('[data-search-term]');
      if (pill) {
        this.input.value = pill.getAttribute('data-search-term');
        this.hideDefaultContent();
        this.input.focus();
        this.getSearchResults(this.getQuery());
        return;
      }

      const link = event.target.closest('a[href]');
      if (link) {
        window.location.href = link.href;
      }
    });
  }

  // Fetch the default content once the page is idle, or straight away when search is about to be used.
  scheduleDefaultContent() {
    const start = () => {
      const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1500));
      idle(() => this.loadDefaultContent(), { timeout: 4000 });
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });

    const summary = this.closest('details')?.querySelector('summary');
    if (summary) {
      ['pointerenter', 'touchstart', 'focus'].forEach((type) => {
        summary.addEventListener(type, () => this.loadDefaultContent(), { once: true, passive: true });
      });
    }
  }

  loadDefaultContent() {
    if (this.defaultContent || !this.defaultSlot) return Promise.resolve();
    if (this.defaultContentRequest) return this.defaultContentRequest;

    const url = this.defaultSlot.dataset.url;
    const cacheKey = `hp-search-default:${url}:${this.defaultSlot.dataset.cacheKey || ''}`;
    let cached = null;
    try { cached = sessionStorage.getItem(cacheKey); } catch (error) {}

    const html = cached
      ? Promise.resolve(cached)
      : fetch(url, { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
          .then((text) => {
            try { sessionStorage.setItem(cacheKey, text); } catch (error) {}
            return text;
          });

    this.defaultContentRequest = html
      .then((text) => {
        const content = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-search-default-content]');
        if (!content || !this.defaultSlot.isConnected) return;
        this.defaultSlot.replaceWith(content);
        this.defaultContent = content;
        this.defaultSlot = null;
        this.bindDefaultContent();
        // Search was opened before the content arrived: show it now, as focus would have
        if (document.activeElement === this.input && !this.getQuery().length) this.showDefaultContent();
      })
      .catch(() => {
        this.defaultContentRequest = null;
      });
    return this.defaultContentRequest;
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      this.showDefaultContent();
      return;
    }

    this.hideDefaultContent();
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    if (!this.defaultContent) this.loadDefaultContent();
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.showDefaultContent();
      return;
    }

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      this.showDefaultContent();
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=4&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.hideDefaultContent();
    this.isOpen = false;
  }

  showDefaultContent() {
    if (!this.defaultContent) return;
    this.predictiveSearchResults.style.display = 'none';
    const availableHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.defaultContent.style.maxHeight = availableHeight;
    this.defaultContent.style.display = 'block';
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  hideDefaultContent() {
    if (!this.defaultContent) return;
    this.defaultContent.style.display = 'none';
    this.predictiveSearchResults.style.display = '';
  }
}

customElements.define('predictive-search', PredictiveSearch);
